import { DisclosurePanel, useDisclosureContext } from '@reach/disclosure';
import { equals, not } from 'ramda';
import React, { ReactNode, forwardRef } from 'react';
import { animated, config, useSpring } from 'react-spring';
import styled from 'styled-components';

import useDivHeight from '@hooks/useDivHeight';

const AnimatedPanel = animated(DisclosurePanel);

const AnimatedAccordionPanel = forwardRef<
	HTMLDivElement,
	{
		children?: ReactNode;
		className?: string;
		defaultOpen?: boolean;
		immediate?: boolean;
		onResolve?: () => void;
	}
>(({ children, className, defaultOpen = false, immediate = false, onResolve }, forwardedRef) => {
	const { open } = useDisclosureContext();
	const { ref, height } = useDivHeight();
	const animation = useSpring({
		from: {
			boxSizing: 'content-box',
			opacity: 1,
			height: defaultOpen ? height : 0,
			overflow: 'hidden',
		},
		to: {
			height: equals(open, defaultOpen) ? 0 : height,
		},
		config: { mass: 1, tension: 150, friction: 24 },
		immediate: immediate,
		onResolve: onResolve,
	});

	return (
		<AnimatedPanel
			as="div"
			className={className}
			style={animation}
			// We need to set hidden to false for the exit animations to work
			// but the panel should still be hidden from the accessibility tree
			// when the panel is closed. We'll use aria-hidden instead.
			hidden={false}
			aria-hidden={equals(open, defaultOpen) || undefined}
			ref={forwardedRef}>
			<div ref={ref}>{children}</div>
		</AnimatedPanel>
	);
});

export default styled(AnimatedAccordionPanel)``;
AnimatedAccordionPanel.displayName = 'AnimatedAccordionPanel';
